import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle2, Hourglass } from 'lucide-react';
import client from '../../api/client';
import { NAVY, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';

const TYPE_LABELS = { vacation: 'Vacaciones', sick: 'Enfermedad', personal: 'Personal' };

const LEVEL_LABELS = {
  1: 'L1 — Supervisor',
  2: 'L2 — Gerente',
  3: 'L3 — Administración',
  4: 'L4 — Recursos Humanos',
  5: 'L5 — Directora',
};

export default function PTOReviewListPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/vacation/my-approvals', { params: { view: 'active' } })
      .then(r => setRecords(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  }

  const pendingCount = records.filter(r => r.status === 'pending').length;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Aprobaciones Pendientes</h1>
        {pendingCount > 0 && (
          <span style={{
            background: '#eab308', color: '#fff', borderRadius: 10,
            padding: '2px 10px', fontSize: 12, fontWeight: 700,
          }}>
            {pendingCount}
          </span>
        )}
      </div>

      {records.length === 0 ? (
        <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          No tiene solicitudes pendientes de aprobación.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {records.map(r => {
            const isPending = r.status === 'pending';
            const isApprovedByMe = r.status === 'approved';
            const isWaiting = r.status === 'waiting';
            const isFullyApproved = r.request_status === 'approved';

            const borderColor = isPending ? '#eab308' : isApprovedByMe ? '#16a34a' : '#94a3b8';
            const iconBg = isPending ? '#fef9c3' : isApprovedByMe ? '#dcfce7' : '#f1f5f9';
            const badgeBg = isPending ? '#ede9fe' : isApprovedByMe ? '#dcfce7' : '#f1f5f9';
            const badgeColor = isPending ? '#6d28d9' : isApprovedByMe ? '#16a34a' : '#64748b';

            return (
              <div
                key={`${r.request_id}-${r.level}`}
                onClick={() => navigate(`/vacaciones/${r.request_id}`)}
                style={{
                  ...bubbleStyle, padding: '14px 18px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 14,
                  borderLeft: `3px solid ${borderColor}`,
                  opacity: isPending ? 1 : 0.85,
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: iconBg, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {isPending ? (
                    <Clock size={18} color="#eab308" />
                  ) : isApprovedByMe ? (
                    <CheckCircle2 size={18} color="#16a34a" />
                  ) : (
                    <Hourglass size={18} color="#94a3b8" />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{r.employee_name}</span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>·</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{TYPE_LABELS[r.type] || r.type}</span>
                    {isFullyApproved && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 8px',
                        borderRadius: 10, background: '#dcfce7', color: '#16a34a',
                      }}>
                        Aprobada
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {formatDate(r.start_date)} — {formatDate(r.end_date)} · {parseFloat(r.days_requested)} día{parseFloat(r.days_requested) !== 1 ? 's' : ''}
                    {isApprovedByMe && (
                      <span style={{ marginLeft: 8, color: '#16a34a', fontWeight: 600 }}>
                        · Aprobado por usted · En nivel {r.current_level}
                      </span>
                    )}
                    {isWaiting && (
                      <span style={{ marginLeft: 8, color: '#64748b', fontWeight: 600 }}>
                        · En espera · Nivel actual: {r.current_level}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{
                  padding: '3px 10px', borderRadius: 10,
                  background: badgeBg, color: badgeColor,
                  fontSize: 11, fontWeight: 600,
                }}>
                  {isPending ? (LEVEL_LABELS[r.level] || `Nivel ${r.level}`) :
                   isWaiting ? `Su turno: ${LEVEL_LABELS[r.level] || `L${r.level}`}` :
                   `Nivel ${r.current_level}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
